import type { ReactNode } from 'react';
import {
  Alert,
  Card as MantineCard,
  Group,
  Loader,
  Text,
  Title,
  type AlertProps,
  type CardProps as MantineCardProps,
  type BoxProps,
  Box,
} from '@mantine/core';
import type { StatusTone } from '../tokens/statusColorMap';
import { StatusBadge } from './StatusBadge';

export function Card({ title, children, ...props }: { title?: string; children: ReactNode } & MantineCardProps) {
  return (
    <MantineCard withBorder shadow="xs" radius="md" p="lg" {...props}>
      {title ? (
        <Title order={2} size="h4" mb="sm">
          {title}
        </Title>
      ) : null}
      {children}
    </MantineCard>
  );
}

/** Lightweight surface without card chrome — prefer over Card for nested workspace regions. */
export function Panel({ children, ...props }: { children: ReactNode } & MantineCardProps) {
  return (
    <MantineCard withBorder shadow="none" radius="md" p="md" {...props}>
      {children}
    </MantineCard>
  );
}

export function Surface({ children, ...props }: { children: ReactNode } & BoxProps) {
  return <Box {...props}>{children}</Box>;
}

export function LoadingState({ label = 'Загрузка данных…' }: { label?: string }) {
  return (
    <Group gap="sm" className="loading-state" role="status" aria-live="polite" data-testid="loading-state">
      <Loader size="sm" />
      <Text>{label}</Text>
    </Group>
  );
}

export function ErrorState({
  message,
  title,
  action,
  ...props
}: {
  message: string;
  title?: string;
  action?: ReactNode;
} & Omit<AlertProps, 'children' | 'title'>) {
  return (
    <Alert color="red" variant="light" radius="md" title={title} data-testid="error-state" {...props}>
      <Text size="sm">{message}</Text>
      {action ? <Box mt="sm">{action}</Box> : null}
    </Alert>
  );
}

export function NoPermissionState({
  message = 'Недостаточно прав для этого действия.',
  title = 'Доступ ограничен',
}: {
  message?: string;
  title?: string;
}) {
  return (
    <Alert color="yellow" variant="light" radius="md" title={title} data-testid="no-permission-state">
      <Text size="sm">{message}</Text>
    </Alert>
  );
}

export function InlineAlert({
  message,
  tone = 'info',
  ...props
}: {
  message: string;
  tone?: StatusTone;
} & Omit<AlertProps, 'children' | 'color'>) {
  const colorMap: Record<StatusTone, string> = {
    success: 'green',
    warning: 'yellow',
    danger: 'red',
    info: 'blue',
    neutral: 'gray',
  };
  return (
    <Alert color={colorMap[tone]} variant="light" radius="md" {...props}>
      {message}
    </Alert>
  );
}

/** @deprecated Prefer MetricCard for dashboard KPI tiles */
export function ImportStatusCard({
  title,
  count,
  description,
  tone = 'info',
}: {
  title: string;
  count: number;
  description: string;
  tone?: StatusTone;
}) {
  return (
    <MantineCard withBorder shadow="xs" radius="md" p="lg" className="import-status-card">
      <StatusBadge label={title} tone={tone} />
      <Title order={3} size="h2" mt="sm">
        {count}
      </Title>
      <Text c="dimmed" mt={4}>
        {description}
      </Text>
    </MantineCard>
  );
}
